// src/engine/connectionValidator.ts
//
// Connection validation logic extracted from useAudioDeviceStore.
// Provides standalone functions for validating connections and checking constraints.

import { Device, Connection, Port } from '@/types'

/**
 * Validate that source and target ports have compatible signal types.
 * Returns true if they match, false otherwise.
 */
export function validateConnection(
  sourcePort: Port,
  targetPort: Port,
): boolean {
  return sourcePort.signalType === targetPort.signalType
}

/**
 * Check whether connecting to a port would violate an exclusive-group
 * constraint. Returns true if a sibling port in the same exclusiveGroup
 * on the same device already has a connection.
 */
export function hasExclusiveGroupConflict(
  device: Device,
  port: Port,
  connections: Connection[],
): boolean {
  if (!port.exclusiveGroup) return false

  // Determine if this port is an input or output
  const isOutput = device.outputs.includes(port)

  const siblingPorts = isOutput
    ? device.outputs.filter(
        p => p.id !== port.id && p.exclusiveGroup === port.exclusiveGroup,
      )
    : device.inputs.filter(
        p => p.id !== port.id && p.exclusiveGroup === port.exclusiveGroup,
      )

  if (siblingPorts.length === 0) return false

  const siblingIds = new Set(siblingPorts.map(p => p.id))

  return connections.some(conn => {
    if (isOutput) {
      return (
        conn.sourceDeviceId === device.id && siblingIds.has(conn.sourcePortId)
      )
    } else {
      return (
        conn.targetDeviceId === device.id && siblingIds.has(conn.targetPortId)
      )
    }
  })
}

/**
 * Get all available ports (not yet connected and not blocked by exclusive groups)
 * for a given device and direction.
 */
export function getAvailablePorts(
  device: Device,
  connections: Connection[],
  direction: 'input' | 'output',
): Port[] {
  const ports = direction === 'input' ? device.inputs : device.outputs
  const usedPortIds = new Set<string>()
  const blockedByExclusiveGroup = new Set<string>()

  // Mark already-connected ports
  if (direction === 'output') {
    for (const conn of connections) {
      if (conn.sourceDeviceId === device.id) {
        usedPortIds.add(conn.sourcePortId)
      }
    }
  } else {
    for (const conn of connections) {
      if (conn.targetDeviceId === device.id) {
        usedPortIds.add(conn.targetPortId)
      }
    }
  }

  // Mark ports blocked by exclusive group constraints
  for (const port of ports) {
    if (port.exclusiveGroup) {
      // If any sibling in the exclusive group is used, block all siblings
      const siblingUsed = ports.some(
        p =>
          p.id !== port.id &&
          p.exclusiveGroup === port.exclusiveGroup &&
          usedPortIds.has(p.id),
      )
      if (siblingUsed) {
        blockedByExclusiveGroup.add(port.id)
      }
    }
  }

  return ports.filter(
    p => !usedPortIds.has(p.id) && !blockedByExclusiveGroup.has(p.id),
  )
}
